// SPDX-License-Identifier: AGPL-3.0-or-later

import {ClientInfo} from '@app/features/app/components/dialogs/components/ClientInfo';
import {LogoutModal} from '@app/features/app/components/dialogs/components/LogoutModal';
import styles from '@app/features/app/components/dialogs/components/MobileSettingsView.module.css';
import {
	MobileHeader,
	MobileHeaderWithBanner,
	MobileSettingsDangerItem,
} from '@app/features/app/components/dialogs/shared/MobileSettingsComponents';
import {LongPressable} from '@app/features/app/components/LongPressable';
import {hasWhatsNewEntries} from '@app/features/app/components/whats_new/WhatsNewEntries';
import {openWhatsNewModal} from '@app/features/app/components/whats_new/WhatsNewModal';
import {usePressable} from '@app/features/app/hooks/usePressable';
import {activateLatestServiceWorker} from '@app/features/platform/types/Versioning';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {MentionBadgeAnimated} from '@app/features/ui/components/MentionBadge';
import {Scroller, type ScrollerHandle} from '@app/features/ui/components/Scroller';
import {Spinner} from '@app/features/ui/components/Spinner';
import userSettingsStyles from '@app/features/user/components/modals/UserSettingsModal.module.css';
import {getSettingsTabComponent} from '@app/features/user/components/settings_utils/DesktopSettingsTabs';
import type {SettingsTab} from '@app/features/user/components/settings_utils/SettingsConstants';
import {getCategoryLabel} from '@app/features/user/components/settings_utils/SettingsConstants';
import type {UserSettingsTabType} from '@app/features/user/components/settings_utils/SettingsSectionRegistry';
import type {MobileNavigationState} from '@app/features/user/hooks/useMobileNavigation';
import {useSettingsContentKey} from '@app/features/user/hooks/useSettingsContentKey';
import {useUnsavedChangesFlash} from '@app/features/user/hooks/useUnsavedChangesFlash';
import Users from '@app/features/user/state/Users';
import {useLingui} from '@lingui/react/macro';
import {ArrowClockwiseIcon, ArrowLeftIcon, type IconWeight, MegaphoneIcon, SignOutIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {AnimatePresence, motion} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import React, {type UIEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';

const logger = new Logger('MobileSettingsView');
interface MobileSettingsViewProps {
	groupedSettingsTabs: Record<string, Array<SettingsTab>>;
	currentTab: SettingsTab | undefined;
	mobileNav: MobileNavigationState;
	onBack: () => void;
	onTabSelect: (tab: string, title: string) => void;
	initialGuildId?: string;
	initialSubtab?: string;
	pendingSection?: string | null;
	onPendingSectionConsumed?: () => void;
}
interface PressableSettingsItemProps {
	tab: SettingsTab;
	onSelect: () => void;
	badge?: React.ReactNode;
}
const PressableSettingsItem: React.FC<PressableSettingsItemProps> = observer(({tab, onSelect, badge}) => {
	const {isPressed, pressableProps} = usePressable();
	const IconComponent = tab.icon;
	return (
		<LongPressable
			className={clsx(styles.settingsItem, isPressed && styles.settingsItemPressed)}
			role="button"
			tabIndex={0}
			onClick={onSelect}
			{...pressableProps}
		>
			<IconComponent className={styles.settingsItemIcon} weight={tab.iconWeight ?? 'fill'} />
			<div className={styles.settingsItemContent}>
				<div className={styles.settingsItemLabelContainer}>
					<span className={styles.settingsItemLabel}>{tab.label}</span>
					{badge}
				</div>
			</div>
			<ArrowLeftIcon className={styles.settingsItemArrow} />
		</LongPressable>
	);
});
interface MobileSettingsActionItemProps {
	icon: React.ComponentType<{className?: string; weight?: IconWeight}>;
	label: React.ReactNode;
	onClick: () => void;
	isLoading?: boolean;
	iconWeight?: IconWeight;
	showArrow?: boolean;
}
const MobileSettingsActionItem: React.FC<MobileSettingsActionItemProps> = observer(
	({icon: IconComponent, label, onClick, isLoading = false, iconWeight, showArrow = true}) => {
		const {isPressed, pressableProps} = usePressable();
		return (
			<LongPressable
				className={clsx(styles.settingsItem, isPressed && styles.settingsItemPressed)}
				role="button"
				tabIndex={0}
				onClick={onClick}
				{...pressableProps}
			>
				<IconComponent className={styles.settingsItemIcon} weight={iconWeight ?? 'regular'} />
				<div className={styles.settingsItemContent}>
					<div className={styles.settingsItemLabelContainer}>
						<span className={styles.settingsItemLabel}>{label}</span>
						{isLoading && <Spinner size="small" className={styles.settingsItemSpinner} />}
					</div>
				</div>
				{showArrow && <ArrowLeftIcon className={styles.settingsItemArrow} />}
			</LongPressable>
		);
	},
);
const ServiceWorkerUpdateButton = observer(() => {
	const {t} = useLingui();
	const [isUpdating, setIsUpdating] = useState(false);
	const handleUpdateServiceWorker = async () => {
		if (isUpdating) return;
		setIsUpdating(true);
		try {
			await activateLatestServiceWorker();
			window.location.reload();
		} catch (error) {
			logger.error('Failed to update service worker:', error);
		} finally {
			setIsUpdating(false);
		}
	};
	return (
		<MobileSettingsActionItem
			icon={ArrowClockwiseIcon}
			iconWeight="bold"
			label={t`Update Service Worker`}
			onClick={handleUpdateServiceWorker}
			isLoading={isUpdating}
			showArrow={false}
		/>
	);
});

const MOBILE_HIDDEN_TAB_TYPES = new Set<UserSettingsTabType>(['keybinds']);
const MobileSettingsList = observer(
	({
		groupedTabs,
		onTabSelect,
		scrollRef,
		onScroll,
	}: {
		groupedTabs: Record<string, Array<SettingsTab>>;
		onTabSelect: (tab: string, title: string) => void;
		scrollRef?: React.Ref<ScrollerHandle>;
		onScroll?: (event: UIEvent<HTMLDivElement>) => void;
	}) => {
		const {t} = useLingui();
		const currentUser = Users.currentUser;
		const shouldShowWhatsNew = hasWhatsNewEntries();
		const mobileVisibleTabs = useMemo(() => {
			const visibleTabs: Record<string, Array<SettingsTab>> = {};
			Object.entries(groupedTabs).forEach(([category, tabs]) => {
				const filteredCategoryTabs = tabs.filter((tab) => !MOBILE_HIDDEN_TAB_TYPES.has(tab.type));
				if (filteredCategoryTabs.length > 0) {
					visibleTabs[category] = filteredCategoryTabs;
				}
			});
			return visibleTabs;
		}, [groupedTabs]);
		const handleLogout = useCallback(() => {
			ModalCommands.push(modal(() => <LogoutModal />));
		}, []);
		const categories = Object.entries(mobileVisibleTabs);
		const lastCategoryIndex = categories.length - 1;
		return (
			<Scroller
				className={styles.scrollerContainer}
				key="mobile-settings-list-scroller"
				ref={scrollRef}
				onScroll={onScroll}
			>
				{categories.map(([category, tabs], categoryIndex) => (
					<div key={category} className={styles.categorySection}>
						<h2 className={styles.categoryTitle}>{getCategoryLabel(category as SettingsTab['category'])}</h2>
						<div className={styles.categoryList}>
							{tabs.map((tab, index) => {
								const isLastTab = index === tabs.length - 1;
								const isLastCategory = categoryIndex === lastCategoryIndex;
								const badge =
									tab.type === 'gift_inventory' && currentUser?.hasUnreadGiftInventory ? (
										<MentionBadgeAnimated mentionCount={currentUser.unreadGiftInventoryCount ?? 1} />
									) : undefined;
								return (
									<div key={tab.type}>
										<PressableSettingsItem tab={tab} onSelect={() => onTabSelect(tab.type, tab.label)} badge={badge} />
										{(!isLastTab || isLastCategory) && <div className={styles.divider} />}
									</div>
								);
							})}
							{categoryIndex === lastCategoryIndex && (
								<>
									{shouldShowWhatsNew && (
										<>
											<MobileSettingsActionItem
												icon={MegaphoneIcon}
												iconWeight="fill"
												label={t`What's New`}
												onClick={openWhatsNewModal}
											/>
											<div className={styles.divider} />
										</>
									)}
									<MobileSettingsDangerItem icon={SignOutIcon} label={t`Log Out`} onClick={handleLogout} />
								</>
							)}
						</div>
					</div>
				))}
				<div className={styles.categorySection}>
					<h2 className={styles.categoryTitle}>{t`Debug`}</h2>
					<div className={styles.categoryList}>
						<ServiceWorkerUpdateButton />
					</div>
				</div>
				<div className={styles.clientInfoContainer}>
					<ClientInfo />
				</div>
			</Scroller>
		);
	},
);
const contentFadeVariants = {
	enter: {opacity: 0},
	center: {opacity: 1},
	exit: {opacity: 0},
};
const headerFadeVariants = {
	enter: {opacity: 0},
	center: {opacity: 1},
	exit: {opacity: 0},
};
interface MobileContentWithScrollSpyProps {
	scrollKey: string;
	initialGuildId?: string;
	initialSubtab?: string;
	currentTabComponent: React.ComponentType<Record<string, unknown>> | null;
	pendingSection?: string | null;
	onPendingSectionConsumed?: () => void;
}
const MobileContentWithScrollSpy: React.FC<MobileContentWithScrollSpyProps> = observer(
	({scrollKey, initialGuildId, initialSubtab, currentTabComponent, pendingSection, onPendingSectionConsumed}) => {
		useEffect(() => {
			if (!pendingSection) return;
			const frame = window.requestAnimationFrame(() => {
				const element = document.getElementById(pendingSection);
				if (element) {
					element.scrollIntoView({behavior: 'auto', block: 'start'});
				}
				onPendingSectionConsumed?.();
			});
			return () => window.cancelAnimationFrame(frame);
		}, [pendingSection, onPendingSectionConsumed]);
		return (
			<Scroller className={styles.scrollerFlex} key={scrollKey} data-settings-scroll-container>
				<div className={styles.contentContainer}>
					{currentTabComponent &&
						React.createElement(currentTabComponent, {
							...(initialGuildId ? {initialGuildId} : {}),
							...(initialSubtab ? {initialSubtab} : {}),
						} as Record<string, unknown>)}
				</div>
			</Scroller>
		);
	},
);
export const MobileSettingsView: React.FC<MobileSettingsViewProps> = observer(
	({
		groupedSettingsTabs,
		currentTab,
		mobileNav,
		onBack,
		onTabSelect,
		initialGuildId,
		initialSubtab,
		pendingSection,
		onPendingSectionConsumed,
	}) => {
		const {t} = useLingui();
		const currentTabId = mobileNav.currentView?.tab;
		const {showUnsavedBanner, flashBanner, tabData, checkUnsavedChanges} = useUnsavedChangesFlash(currentTabId);
		const {contentKey} = useSettingsContentKey();
		const [initialPendingSection, setInitialPendingSection] = useState<string | null>(() => initialSubtab ?? null);
		useEffect(() => {
			setInitialPendingSection(initialSubtab ?? null);
		}, [initialSubtab]);
		const scrollKey = useMemo(() => {
			if (!currentTabId) {
				return 'user-settings-mobile-root';
			}
			const subtabKey = contentKey ?? initialSubtab ?? 'root';
			return `user-settings-${currentTabId}-${subtabKey}`;
		}, [contentKey, currentTabId, initialSubtab]);
		const handleBack = useCallback(() => {
			if (checkUnsavedChanges()) return;
			onBack();
		}, [checkUnsavedChanges, onBack]);
		const handleTabSelect = useCallback(
			(tab: string, title: string) => {
				if (checkUnsavedChanges()) return;
				onTabSelect(tab, title);
			},
			[checkUnsavedChanges, onTabSelect],
		);
		const pendingScrollSection = pendingSection ?? initialPendingSection;
		const handlePendingSectionConsumed = useCallback(() => {
			if (pendingSection) {
				onPendingSectionConsumed?.();
			}
			if (initialPendingSection) {
				setInitialPendingSection(null);
			}
		}, [initialPendingSection, onPendingSectionConsumed, pendingSection]);
		const showMobileList = mobileNav.isRootView;
		const showMobileContent = !mobileNav.isRootView;
		const listScrollPositionRef = useRef(0);
		const listScrollerRef = useRef<ScrollerHandle | null>(null);
		const handleListScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
			listScrollPositionRef.current = event.currentTarget.scrollTop;
		}, []);
		useEffect(() => {
			if (!showMobileList) return;
			const scroller = listScrollerRef.current;
			if (!scroller) return;
			const target = listScrollPositionRef.current;
			if (target === 0) return;
			scroller.scrollTo({to: target, animate: false});
		}, [showMobileList]);
		const currentTabComponent = currentTab ? getSettingsTabComponent(currentTab.type) : null;
		return (
			<div className={userSettingsStyles.mobileWrapper}>
				<div className={userSettingsStyles.mobileHeaderContainer}>
					<AnimatePresence mode="wait" custom={mobileNav.direction}>
						{showMobileList && (
							<motion.div
								key="mobile-list-header"
								variants={headerFadeVariants}
								initial="center"
								animate="center"
								exit="exit"
								transition={{duration: 0.08, ease: 'easeInOut'}}
								className={userSettingsStyles.mobileHeaderContent}
							>
								<MobileHeader title={t`Settings`} onBack={() => ModalCommands.pop()} />
							</motion.div>
						)}
						{showMobileContent && currentTab && (
							<motion.div
								key={`mobile-content-header-${mobileNav.currentView?.tab}`}
								variants={headerFadeVariants}
								initial="enter"
								animate="center"
								exit="exit"
								transition={{duration: 0.08, ease: 'easeInOut'}}
								className={userSettingsStyles.mobileHeaderContent}
							>
								<MobileHeaderWithBanner
									title={mobileNav.currentView?.title || currentTab.label}
									onBack={handleBack}
									showUnsavedBanner={showUnsavedBanner}
									flashBanner={flashBanner}
									tabData={tabData}
								/>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
				<div className={userSettingsStyles.mobileContentContainer}>
					<AnimatePresence mode="wait" custom={mobileNav.direction}>
						{showMobileList && (
							<motion.div
								key="mobile-list-content"
								custom={mobileNav.direction}
								variants={contentFadeVariants}
								initial="center"
								animate="center"
								exit="exit"
								transition={{duration: 0.15, ease: 'easeInOut'}}
								className={userSettingsStyles.mobileContentPane}
								style={{willChange: 'transform'}}
							>
								<MobileSettingsList
									groupedTabs={groupedSettingsTabs}
									onTabSelect={handleTabSelect}
									scrollRef={listScrollerRef}
									onScroll={handleListScroll}
								/>
							</motion.div>
						)}
						{showMobileContent && currentTab && (
							<motion.div
								key={`mobile-content-${mobileNav.currentView?.tab}`}
								custom={mobileNav.direction}
								variants={contentFadeVariants}
								initial="enter"
								animate="center"
								exit="exit"
								transition={{duration: 0.15, ease: 'easeInOut'}}
								className={userSettingsStyles.mobileContentPane}
								style={{willChange: 'transform'}}
							>
								<MobileContentWithScrollSpy
									scrollKey={scrollKey}
									initialGuildId={initialGuildId}
									initialSubtab={initialSubtab}
									currentTabComponent={currentTabComponent}
									pendingSection={pendingScrollSection}
									onPendingSectionConsumed={handlePendingSectionConsumed}
								/>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			</div>
		);
	},
);
